import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import API_BASE_URL from '../config';

interface Slot {
    date: string;
    status: 'available' | 'booked' | 'blocked';
}

interface PublicAvailabilityCalendarProps {
    vendorId: string;
    selectedFromDate?: string;
    selectedToDate?: string;
    onDateSelect: (fromDate: string, toDate: string) => void;
    className?: string;
}

const PublicAvailabilityCalendar: React.FC<PublicAvailabilityCalendarProps> = ({ 
    vendorId, selectedFromDate, selectedToDate, onDateSelect, className = '' 
}) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (vendorId) {
            fetchVendorSlots();
        }
    }, [vendorId]);

    const sanitizeVendorId = (id: string) => {
        if (!id) return '';
        if (id.startsWith('prod_')) {
            return id.split('_')[1];
        }
        return id;
    };

    const fetchVendorSlots = async () => {
        setFetching(true);
        try {
            const sanitizedId = sanitizeVendorId(vendorId);
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${sanitizedId}/availability`);
            const data = await res.json();
            if (data.success) {
                setSlots(data.availability.map((s: any) => ({
                    date: s.date.split('T')[0],
                    status: s.status
                })));
            }
        } catch (error) {
            console.error('Error fetching availability:', error);
        } finally {
            setFetching(false);
        }
    };

    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

    const onDateClick = (dateStr: string, isBooked: boolean) => {
        if (isBooked) return;
        
        let newFrom = selectedFromDate || '';
        let newTo = selectedToDate || '';

        if (!newFrom || (newFrom && newTo)) {
            newFrom = dateStr;
            newTo = '';
        } else {
            if (dateStr < newFrom) {
                newFrom = dateStr;
                newTo = '';
            } else {
                newTo = dateStr;
            }
        }
        onDateSelect(newFrom, newTo);
    };

    const renderDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        
        const days = [];
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="cal-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const slot = slots.find(s => s.date === dateStr);
            const isBooked = slot?.status === 'booked' || slot?.status === 'blocked';
            const isSelected = dateStr === selectedFromDate || dateStr === selectedToDate || 
                              (selectedFromDate && selectedToDate && dateStr > selectedFromDate && dateStr < selectedToDate);
            const isToday = new Date().toISOString().split('T')[0] === dateStr;

            days.push(
                <div 
                    key={day} 
                    className={`cal-day ${isBooked ? 'booked' : 'available'} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                    onClick={() => onDateClick(dateStr, isBooked)}
                >
                    {day}
                </div>
            );
        }
        return days;
    };

    return (
        <div className={`public-avail-calendar ${className}`}>
            <div className="cal-header">
                <button onClick={handlePrevMonth} type="button"><ChevronLeft size={18} /></button>
                <div className="cal-selectors">
                    <select 
                        value={currentDate.getMonth()} 
                        onChange={(e) => setCurrentDate(new Date(currentDate.getFullYear(), parseInt(e.target.value), 1))}
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                        ))}
                    </select>
                    <select 
                        value={currentDate.getFullYear()} 
                        onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), currentDate.getMonth(), 1))}
                    >
                        {Array.from({ length: 5 }, (_, i) => (
                            <option key={i} value={new Date().getFullYear() + i}>{new Date().getFullYear() + i}</option>
                        ))}
                    </select>
                </div>
                <button onClick={handleNextMonth} type="button"><ChevronRight size={18} /></button>
            </div>
            <div className="cal-weekdays">
                {['S','M','T','W','T','F','S'].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="cal-grid">
                {fetching ? <div className="cal-loading">Loading...</div> : renderDays()}
            </div>
            <div className="cal-legend-lite">
                <div className="leg-item"><span className="dot booked"></span> Booked</div>
                <div className="leg-item"><span className="dot available"></span> Available</div>
            </div>
        </div>
    );
};

export default PublicAvailabilityCalendar;
